import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Unstable_Grid2 as Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  FitnessCenter as ExerciseIcon,
  MoreVert as MoreVertIcon,
  Timer as TimerIcon,
  Repeat as RepsIcon,
  Scale as WeightIcon,
  DirectionsRun as DistanceIcon,
  PublicOutlined as GlobalIcon
} from '@mui/icons-material';
import exerciseSlice from 'store/modules/exercise';
import requestStatus from 'common/constants/status';

// Get parameter icon
const getParameterIcon = (type) => {
  switch (type) {
    case 'TIME':
      return <TimerIcon sx={{ fontSize: 16, color: '#F9BD4E' }} />;
    case 'REPS':
      return <RepsIcon sx={{ fontSize: 16, color: '#F9BD4E' }} />;
    case 'WEIGHT':
      return <WeightIcon sx={{ fontSize: 16, color: '#F9BD4E' }} />;
    case 'DISTANCE':
      return <DistanceIcon sx={{ fontSize: 16, color: '#F9BD4E' }} />;
    default:
      return null;
  }
};

// Format parameter value for display
const formatParameterValue = (value, type) => {
  if (!value && value !== 0) return '-';

  switch (type) {
    case 'TIME': {
      const minutes = Math.floor(value / 60);
      const seconds = value % 60;
      return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
    }
    case 'REPS':
      return `${value} reps`;
    case 'WEIGHT':
      return `${value} kg`;
    case 'DISTANCE':
      return `${value} km`;
    default:
      return value;
  }
};

// Exercise Card Component - Shows added exercise with sets
function ExerciseCard({ exercise, exerciseData, onRemove, onEdit }) {
  const { t } = useTranslation(['performances']);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const calculatePercentage = (set) => {
    let completed = 0;
    const total = exercise.parameters.length || 1;

    exercise.parameters.forEach((param) => {
      const paramType = param.parameter_type;
      if (paramType === 'TIME' && set.time_seconds > 0) completed += 1;
      if (paramType === 'REPS' && set.repetitions > 0) completed += 1;
      if (paramType === 'WEIGHT' && set.weight_kg > 0) completed += 1;
      if (paramType === 'DISTANCE' && set.distance_km > 0) completed += 1;
    });

    return Math.round((completed / total) * 100);
  };

  return (
    <Card
      sx={{
        bgcolor: 'surface.raised',
        border: '1px solid rgba(249, 189, 78, 0.2)',
        transition: 'all 0.3s ease',
        height: '100%',
        '&:hover': {
          border: '1px solid #F9BD4E',
          boxShadow: '0 4px 12px rgba(249, 189, 78, 0.2)'
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <ExerciseIcon sx={{ color: '#F9BD4E', fontSize: 20 }} />
            <Typography
              variant="subtitle1"
              color="text.primary"
              fontWeight="bold"
              noWrap
              title={exercise.localized_name || exercise.name}
            >
              {exercise.localized_name || exercise.name}
            </Typography>
          </Stack>
          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ color: 'text.secondary' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            slotProps={{
              paper: {
                sx: {
                  bgcolor: 'surface.content',
                  border: '1px solid rgba(249, 189, 78, 0.2)',
                  minWidth: 140,
                  maxWidth: 180
                }
              }
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={() => { setAnchorEl(null); onEdit(); }}
              sx={{
                color: 'text.primary',
                '&:hover': { bgcolor: 'rgba(249, 189, 78, 0.1)' }
              }}
            >
              <ListItemIcon><EditIcon fontSize="small" sx={{ color: '#F9BD4E' }} /></ListItemIcon>
              Editar
            </MenuItem>
            <MenuItem
              onClick={() => { setAnchorEl(null); onRemove(); }}
              sx={{
                color: '#f44336',
                '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' }
              }}
            >
              <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#f44336' }} /></ListItemIcon>
              Eliminar
            </MenuItem>
          </Menu>
        </Stack>

        {exerciseData.observations && (
          <Typography variant="caption" color="text.secondary" mb={1} fontStyle="italic" display="block" noWrap>
            {exerciseData.observations}
          </Typography>
        )}

        {/* Sets */}
        <Stack spacing={1}>
          {exerciseData.sets.map((set, setIndex) => {
            const percentage = calculatePercentage(set);

            return (
              <Box
                key={set.id || `set-${setIndex}`}
                sx={{
                  bgcolor: 'surface.app',
                  border: '1px solid rgba(249, 189, 78, 0.1)',
                  borderRadius: 1,
                  p: 1.5
                }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Chip
                      label={t('dynamicPerformances.modal.setNumber', { ns: 'performances', number: setIndex + 1 })}
                      size="small"
                      sx={{
                        bgcolor: '#F9BD4E',
                        color: '#181d36',
                        fontWeight: 'bold',
                        height: 22,
                        fontSize: '0.7rem'
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {percentage}%
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: 'text.disabled',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#F9BD4E',
                        borderRadius: 2
                      }
                    }}
                  />

                  {exercise.parameters.map((param) => {
                    let value = null;
                    switch (param.parameter_type) {
                      case 'TIME':
                        value = set.time_seconds;
                        break;
                      case 'REPS':
                        value = set.repetitions;
                        break;
                      case 'WEIGHT':
                        value = set.weight_kg;
                        break;
                      case 'DISTANCE':
                        value = set.distance_km;
                        break;
                      default:
                        break;
                    }

                    if (!value && value !== 0) return null;

                    return (
                      <Stack
                        key={param.id}
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{
                          bgcolor: 'rgba(249, 189, 78, 0.05)',
                          px: 1,
                          py: 0.5,
                          borderRadius: 0.5
                        }}
                      >
                        {getParameterIcon(param.parameter_type)}
                        <Typography variant="caption" color="text.primary" fontWeight="500">
                          {formatParameterValue(value, param.parameter_type)}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

ExerciseCard.propTypes = {
  exercise: PropTypes.object.isRequired,
  exerciseData: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired
};

// Add Exercise Modal Component
function AddExerciseModal({ open, onClose, exercises, onSave, editData = null, t }) {
  const [selectedExerciseId, setSelectedExerciseId] = useState('');
  const [sets, setSets] = useState([]);
  const [observations, setObservations] = useState('');

  const isEditing = Boolean(editData);

  // Find selected exercise (exercises are already filtered to dynamic only)
  const selectedExercise = exercises.find((e) => String(e.id) === String(selectedExerciseId));

  useEffect(() => {
    if (open && editData) {
      // Pre-fill form with existing data
      setSelectedExerciseId(String(editData.exercise_id));
      setObservations(editData.observations || '');
      setSets(
        editData.sets.map((set) => ({
          id: `${Date.now()}-${Math.random()}`,
          time_seconds: set.time_seconds ?? '',
          repetitions: set.repetitions ?? '',
          weight_kg: set.weight_kg ?? '',
          distance_km: set.distance_km ?? ''
        }))
      );
    } else if (!open) {
      // Reset form when modal closes
      setSelectedExerciseId('');
      setSets([]);
      setObservations('');
    }
  }, [open, editData]);

  // Auto-add first set when exercise is selected (only for new)
  useEffect(() => {
    if (selectedExercise && sets.length === 0 && !isEditing) {
      setSets([
        {
          id: `${Date.now()}-${Math.random()}`,
          time_seconds: '',
          repetitions: '',
          weight_kg: '',
          distance_km: ''
        }
      ]);
    }
  }, [selectedExercise, sets.length, isEditing]);

  const handleAddSet = () => {
    setSets([
      ...sets,
      {
        id: `${Date.now()}-${Math.random()}`,
        time_seconds: '',
        repetitions: '',
        weight_kg: '',
        distance_km: ''
      }
    ]);
  };

  const handleRemoveSet = (index) => setSets(sets.filter((_, i) => i !== index));

  const handleSetChange = (index, field, value) => {
    const newSets = [...sets];
    newSets[index] = { ...newSets[index], [field]: value };
    setSets(newSets);
  };

  const handleSave = () => {
    if (!selectedExerciseId || sets.length === 0) return;

    const validSets = sets
      .filter((set) => set.time_seconds || set.repetitions || set.weight_kg || set.distance_km)
      .map((set) => ({
        time_seconds: set.time_seconds ? parseInt(set.time_seconds, 10) : null,
        repetitions: set.repetitions ? parseInt(set.repetitions, 10) : null,
        weight_kg: set.weight_kg ? parseFloat(set.weight_kg) : null,
        distance_km: set.distance_km ? parseFloat(set.distance_km) : null
      }));

    if (validSets.length === 0) return;

    const dataToSave = {
      exercise_id: parseInt(selectedExerciseId, 10),
      sets: validSets,
      observations
    };

    onSave(dataToSave);
    onClose();
  };

  // Check if there's at least one set with at least one field filled
  const hasValidSets = sets.some(
    (set) => set.time_seconds || set.repetitions || set.weight_kg || set.distance_km
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'surface.content',
          border: '1px solid rgba(249, 189, 78, 0.3)',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(249, 189, 78, 0.2)', pb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <ExerciseIcon sx={{ color: '#F9BD4E', fontSize: 28 }} />
            <Typography variant="h6" color="text.primary" fontWeight="bold">
              {isEditing ? 'Editar ejercicio' : t('addExercise', { ns: 'buttons' })}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Exercise Select */}
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body1"
              sx={{ mb: 1, color: 'text.primary', fontWeight: 500 }}
            >
              {t('exercise', { ns: 'labels' })} *
            </Typography>
            {/* Búsqueda escribiendo (type-to-search). Los ejercicios OTRIXVERSE (suben en el ranking)
                se marcan con un badge dorado "VERSE" para identificarlos. */}
            <Autocomplete
              options={exercises}
              value={selectedExercise || null}
              onChange={(_, v) => setSelectedExerciseId(v ? String(v.id) : '')}
              disabled={isEditing}
              getOptionLabel={(o) => o?.localized_name || o?.name || ''}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
              noOptionsText={t('noDynamicExercises', { ns: 'labels', defaultValue: 'No hay ejercicios dinámicos disponibles' })}
              renderInput={(params) => (
                <TextField {...params} placeholder={t('selectExercise', { ns: 'labels' })} />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>{option.localized_name || option.name}</Typography>
                  {option.is_otrixverse && (
                    <Chip
                      size="small"
                      icon={<GlobalIcon sx={{ fontSize: 13 }} />}
                      label="VERSE"
                      sx={{ height: 20, bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700, fontSize: '0.62rem', '& .MuiChip-icon': { color: 'gold.main' } }}
                    />
                  )}
                </Box>
              )}
            />
            {selectedExercise?.is_otrixverse && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
                <GlobalIcon sx={{ fontSize: 14, color: 'gold.main' }} />
                <Typography variant="caption" sx={{ color: 'gold.main', fontWeight: 600 }}>
                  {t('otrixverseExerciseHint', { ns: 'labels', defaultValue: 'Este ejercicio suma en el Ranking Global (OTRIXVERSE).' })}
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Sets Section */}
          {selectedExercise && (
            <>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" color="#F9BD4E" fontWeight="bold">
                    {t('dynamicPerformances.modal.sets', { ns: 'performances' })}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddSet}
                    sx={{
                      bgcolor: '#F9BD4E',
                      color: '#181d36',
                      fontWeight: 'bold',
                      '&:hover': {
                        bgcolor: '#ffd166'
                      }
                    }}
                  >
                    {t('addSet', { ns: 'buttons' })}
                  </Button>
                </Stack>

                {sets.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    {t('noSets', { ns: 'labels' })}
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {sets.map((set, setIndex) => (
                      <Card
                        key={set.id}
                        sx={{
                          bgcolor: 'surface.app',
                          border: '1px solid rgba(249, 189, 78, 0.2)'
                        }}
                      >
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Chip
                              label={t('dynamicPerformances.modal.setNumber', { ns: 'performances', number: setIndex + 1 })}
                              size="small"
                              sx={{
                                bgcolor: '#F9BD4E',
                                color: '#181d36',
                                fontWeight: 'bold'
                              }}
                            />
                            {sets.length > 1 && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveSet(setIndex)}
                                sx={{ color: '#f44336' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>

                          <Grid container spacing={2}>
                            {selectedExercise.parameters.map((param) => {
                              let fieldName = '';
                              let label = '';
                              let unit = '';

                              switch (param.parameter_type) {
                                case 'TIME':
                                  fieldName = 'time_seconds';
                                  label = t('timeDuration', { ns: 'labels' });
                                  unit = 'seg';
                                  break;
                                case 'REPS':
                                  fieldName = 'repetitions';
                                  label = t('repetitions', { ns: 'labels' });
                                  unit = '';
                                  break;
                                case 'WEIGHT':
                                  fieldName = 'weight_kg';
                                  label = t('weight', { ns: 'labels' });
                                  unit = 'kg';
                                  break;
                                case 'DISTANCE':
                                  fieldName = 'distance_km';
                                  label = t('distance', { ns: 'labels' });
                                  unit = 'km';
                                  break;
                                default:
                                  return null;
                              }

                              return (
                                <Grid item xs={6} sm={3} key={param.id}>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    label={label}
                                    value={set[fieldName] || ''}
                                    onChange={(e) => handleSetChange(setIndex, fieldName, e.target.value)}
                                    InputProps={{
                                      endAdornment: unit ? (
                                        <InputAdornment position="end">
                                          <Typography color="text.secondary">{unit}</Typography>
                                        </InputAdornment>
                                      ) : undefined
                                    }}
                                    sx={{
                                      '& .MuiInputLabel-root': {
                                        color: 'text.secondary',
                                        '&.Mui-focused': { color: '#F9BD4E' }
                                      },
                                      '& .MuiOutlinedInput-root': {
                                        color: 'text.primary',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                        '&:hover fieldset': { borderColor: '#F9BD4E' },
                                        '&.Mui-focused fieldset': { borderColor: '#F9BD4E' }
                                      }
                                    }}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>

              {/* Observations */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('observations', { ns: 'labels' })}
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                placeholder={t('observationsPlaceholder', { ns: 'labels' })}
                sx={{
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                    '&.Mui-focused': { color: '#F9BD4E' }
                  },
                  '& .MuiOutlinedInput-root': {
                    color: 'text.primary',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#F9BD4E' },
                    '&.Mui-focused fieldset': { borderColor: '#F9BD4E' }
                  }
                }}
              />
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(249, 189, 78, 0.2)', p: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            '&:hover': { bgcolor: 'text.disabled' }
          }}
        >
          {t('cancel', { ns: 'buttons' })}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!selectedExerciseId || !hasValidSets}
          sx={{
            bgcolor: '#F9BD4E',
            color: '#181d36',
            fontWeight: 'bold',
            '&:hover': {
              bgcolor: '#ffd166'
            },
            '&:disabled': {
              bgcolor: 'rgba(249, 189, 78, 0.3)',
              color: 'rgba(24, 29, 54, 0.5)'
            }
          }}
        >
          {t('save', { ns: 'buttons' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddExerciseModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  exercises: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  editData: PropTypes.object,
  t: PropTypes.func.isRequired
};

// Main component
function DynamicExerciseFields({ onExercisesChange = null, selectedClient = null }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['labels', 'buttons']);

  const exercises = useSelector((state) => state.exercise.data.exercises);
  const status = useSelector((state) => state.exercise.status.list);

  const [exerciseData, setExerciseData] = useState(() => {
    const saved = sessionStorage.getItem('otrix_draft_exercises');
    return saved ? JSON.parse(saved) : [];
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  // Persist exercises to sessionStorage
  useEffect(() => {
    if (exerciseData.length > 0) {
      sessionStorage.setItem('otrix_draft_exercises', JSON.stringify(exerciseData));
    } else {
      sessionStorage.removeItem('otrix_draft_exercises');
    }
  }, [exerciseData]);

  useEffect(() => {
    if (status === requestStatus.idle) {
      dispatch(exerciseSlice.actions.list());
    }
  }, [dispatch, status]);

  // Notify parent when exercises change (pass raw data with sets structure)
  useEffect(() => {
    if (onExercisesChange) {
      onExercisesChange(exerciseData);
    }
  }, [exerciseData, onExercisesChange]);

  const handleSaveExercise = useCallback((savedExercise) => {
    if (editingIndex !== null) {
      // Editing existing exercise
      setExerciseData((prev) => {
        const updated = [...prev];
        updated[editingIndex] = { ...updated[editingIndex], ...savedExercise };
        return updated;
      });
      setEditingIndex(null);
    } else {
      // Adding new exercise
      setExerciseData((prev) => [
        ...prev,
        { ...savedExercise, id: `${Date.now()}-${Math.random()}` }
      ]);
    }
  }, [editingIndex]);

  const handleRemoveExercise = useCallback((index) => {
    setExerciseData((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleEditExercise = useCallback((index) => {
    setEditingIndex(index);
    setModalOpen(true);
  }, []);

  const isLoading = status === requestStatus.loading;

  // Filter dynamic exercises (with parameters)
  const dynamicExercises = exercises.filter((ex) => ex.parameters && ex.parameters.length > 0);

  const isButtonDisabled =
    !selectedClient ||
    selectedClient?.membership_status !== 'ACTIVE' ||
    dynamicExercises.length === 0;

  return (
    <Box>
      {/* Title + Add Button in same row when there are cards */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: '35px', mb: 1 }}>
        <Typography
          component="h4"
          sx={{ fontSize: (theme) => theme.typography.pxToRem(26) }}
        >
          {t('exercises', { ns: 'labels' })}
        </Typography>
        {exerciseData.length > 0 && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
            disabled={isButtonDisabled}
            sx={{
              bgcolor: '#F9BD4E',
              color: '#181d36',
              fontWeight: 'bold',
              px: 2.5,
              py: 0.8,
              fontSize: '0.85rem',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(249, 189, 78, 0.3)',
              '&:hover': {
                bgcolor: '#ffd166',
                boxShadow: '0 6px 16px rgba(249, 189, 78, 0.4)',
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                bgcolor: 'rgba(249, 189, 78, 0.3)',
                color: 'rgba(24, 29, 54, 0.5)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('addExercise', { ns: 'buttons' })}
          </Button>
        )}
      </Stack>
      <Divider sx={{ marginBottom: '22px' }} />

      {isLoading ? (
        <Typography color="text.secondary">{t('loading', { ns: 'labels' })}...</Typography>
      ) : (
        <>
          {/* Info message if no dynamic exercises */}
          {dynamicExercises.length === 0 && exerciseData.length === 0 && (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                bgcolor: 'rgba(249, 189, 78, 0.1)',
                borderRadius: 2,
                border: '1px solid rgba(249, 189, 78, 0.3)',
                mb: 3
              }}
            >
              <Typography color="text.secondary">
                No hay ejercicios dinámicos disponibles. Por favor, crea ejercicios con parámetros
                primero.
              </Typography>
            </Box>
          )}

          {/* Exercise Cards Grid - 3 columns */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: exerciseData.length === 2 ? 'center' : 'flex-start'
            }}
          >
            {exerciseData.map((item, index) => {
              const exercise = exercises.find((e) => String(e.id) === String(item.exercise_id));
              if (!exercise) return null;

              return (
                <Box
                  key={item.id || `exercise-${item.exercise_id}-${index}`}
                  sx={{
                    width: {
                      xs: '100%',
                      sm: 'calc(50% - 8px)',
                      md: 'calc(33.33% - 11px)'
                    },
                    minWidth: 0
                  }}
                >
                  <ExerciseCard
                    exercise={exercise}
                    exerciseData={item}
                    onRemove={() => handleRemoveExercise(index)}
                    onEdit={() => handleEditExercise(index)}
                  />
                </Box>
              );
            })}
          </Box>

          {/* Empty state - centered button */}
          {exerciseData.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => setModalOpen(true)}
                disabled={isButtonDisabled}
                sx={{
                  bgcolor: '#F9BD4E',
                  color: '#181d36',
                  fontWeight: 'bold',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(249, 189, 78, 0.3)',
                  '&:hover': {
                    bgcolor: '#ffd166',
                    boxShadow: '0 6px 16px rgba(249, 189, 78, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(249, 189, 78, 0.3)',
                    color: 'rgba(24, 29, 54, 0.5)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {t('addExercise', { ns: 'buttons' })}
              </Button>
            </Box>
          )}

          {/* Modal */}
          <AddExerciseModal
            open={modalOpen}
            onClose={() => { setModalOpen(false); setEditingIndex(null); }}
            exercises={dynamicExercises}
            onSave={handleSaveExercise}
            editData={editingIndex !== null ? exerciseData[editingIndex] : null}
            t={t}
          />
        </>
      )}
    </Box>
  );
}

DynamicExerciseFields.propTypes = {
  onExercisesChange: PropTypes.func,
  selectedClient: PropTypes.object
};

export default DynamicExerciseFields;
